import * as React from "react";
import { useLocation, useNavigationType } from "react-router";

// In-App-Verlaufsstack für die Topbar-Buttons „Zurück"/„Vorwärts".
//
// Die Browser-History bietet keine verlässliche Vorwärts-Introspektion und keine
// Titel-Information über die Nachbar-Einträge. Für ehrliche Buttons (ausgrauen
// ohne Ziel + grober Hover-Hinweis „Zurück zu Kontakte") führen wir daher einen
// eigenen, an `window.history.state.idx` verankerten Stack.
//
// Verankerung an `idx` (react-router v7 legt einen monotonen Index in
// `window.history.state` ab): Die Array-Position im Stack ENTSPRICHT dem
// Router-`idx`. Damit ist der Stack automatisch mit Browser-Zurück/-Vorwärts
// (POP) synchron, ohne dass wir raten müssen.
//
// Bewusst React-arm: die gesamte Entscheidungslogik (`navHistoryReduce`,
// `routeLabel`, Selektoren) ist pur und im Vitest-„node"-Runner ohne DOM
// testbar; `useNavHistory` ist nur dünner Glue um useLocation/useNavigationType.

/** Ein Eintrag im In-App-Verlauf: Pfad + grobes Bereichs-/Entitäts-Label. */
export interface NavEntry {
  pathname: string;
  label: string;
}

/**
 * Der Verlaufsstack. `entries` ist an den Router-`idx` indiziert (kann Lücken
 * haben, wenn die App tief in einer bestehenden Browser-Session betreten wurde
 * → `undefined`). `index` ist die aktuelle Position (== Router-`idx`).
 */
export interface NavHistoryState {
  entries: (NavEntry | undefined)[];
  index: number;
}

export type NavEventType = "PUSH" | "REPLACE" | "POP";

export interface NavEvent {
  type: NavEventType;
  pathname: string;
  label: string;
  /** window.history.state.idx zum Zeitpunkt der Navigation. */
  idx: number;
}

/** Leerer Startzustand (frischer Tab / vor der ersten Navigation). */
export const initialNavHistory: NavHistoryState = { entries: [], index: -1 };

/** idx defensiv normalisieren (nur nicht-negative Ganzzahlen, sonst 0). */
function normalizeIdx(idx: number): number {
  return Number.isInteger(idx) && idx >= 0 ? idx : 0;
}

/**
 * Reiner Reducer: verrechnet ein Navigations-Ereignis in den Stack.
 * - PUSH: Eintrag an Position `idx` setzen und die **Zukunft kappen** (alles ab
 *   `idx` verwerfen) — genau wie der Browser die Vorwärts-History nach einem
 *   Push verwirft.
 * - REPLACE: Eintrag an `idx` ersetzen, Rest bleibt.
 * - POP: nur die Position wechseln (Vorwärts-History bleibt erhalten); fehlt der
 *   Eintrag (z.B. nach Hard-Reload), wird er best-effort gefüllt.
 */
export function navHistoryReduce(
  state: NavHistoryState,
  ev: NavEvent,
): NavHistoryState {
  const idx = normalizeIdx(ev.idx);
  const entry: NavEntry = { pathname: ev.pathname, label: ev.label };

  if (ev.type === "PUSH") {
    const entries = state.entries.slice(0, idx);
    entries[idx] = entry;
    return { entries, index: idx };
  }

  if (ev.type === "REPLACE") {
    const entries = state.entries.slice();
    entries[idx] = entry;
    return { entries, index: idx };
  }

  // POP
  const entries = state.entries.slice();
  if (!entries[idx]) entries[idx] = entry;
  return { entries, index: idx };
}

/**
 * „Zurück" ist möglich, wenn es einen bekannten Vorgänger im Stack gibt.
 * Nach Hard-Reload ist nur der aktuelle Eintrag bekannt → false (bewusste
 * fail-safe-Vereinfachung: erst nach der ersten In-App-Navigation aktiv).
 */
export function canGoBack(state: NavHistoryState): boolean {
  return state.index > 0 && !!state.entries[state.index - 1];
}

/** „Vorwärts" ist möglich, wenn ein bekannter Nachfolger im Stack liegt. */
export function canGoForward(state: NavHistoryState): boolean {
  return state.index >= 0 && !!state.entries[state.index + 1];
}

/** Grobes Label des Vorgänger-Eintrags (für „Zurück zu …") oder null. */
export function backLabel(state: NavHistoryState): string | null {
  return canGoBack(state) ? state.entries[state.index - 1]!.label : null;
}

/** Grobes Label des Nachfolger-Eintrags (für „Vorwärts zu …") oder null. */
export function forwardLabel(state: NavHistoryState): string | null {
  return canGoForward(state) ? state.entries[state.index + 1]!.label : null;
}

// ---------------------------------------------------------------------------
// routeLabel: grobes Bereichs-/Entitäts-Label pro Pfad (für den Hover-Hinweis).
// Listen-Route → Bereichstitel (z.B. „Kontakte"); Detail-Route (mit Segment
// dahinter) → Entitätsname im Singular (z.B. „Kontakt"). Bewusst grob — nur
// Bereich/Entität, nie die konkrete URL.
// ---------------------------------------------------------------------------

interface AreaSpec {
  /** Route-Präfixe, die diesen Bereich matchen. */
  prefixes: string[];
  /** Label der Listen-/Übersichts-Route. */
  list: string;
  /** Label einer Detail-Route (Segment hinter dem Präfix); fehlt = wie list. */
  detail?: string;
}

const AREAS: AreaSpec[] = [
  { prefixes: ["/contacts", "/kontakte"], list: "Kontakte", detail: "Kontakt" },
  { prefixes: ["/risks"], list: "Risiken", detail: "Risiko" },
  { prefixes: ["/contracts"], list: "Verträge", detail: "Vertrag" },
  { prefixes: ["/damages", "/schaeden"], list: "Schäden", detail: "Schaden" },
  { prefixes: ["/vorgaenge"], list: "Vorgänge", detail: "Vorgang" },
  { prefixes: ["/todos"], list: "Todos", detail: "Todo" },
  { prefixes: ["/termine"], list: "Termine" },
  { prefixes: ["/emails"], list: "Schriftverkehr" },
  { prefixes: ["/anrufe"], list: "Telefon" },
  { prefixes: ["/chat"], list: "Interner Chat" },
  { prefixes: ["/alte-kundenakten"], list: "Alte Kundenakten" },
  { prefixes: ["/settings", "/einstellungen"], list: "Einstellungen" },
  { prefixes: ["/profil"], list: "Profil" },
];

/** Grobes Label für einen Pfad. Unbekannt → generisches „Seite". */
export function routeLabel(pathname: string): string {
  // Trailing-Slash entfernen (außer Root).
  const p =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  if (p === "/" || p === "") return "Dashboard";
  for (const spec of AREAS) {
    for (const prefix of spec.prefixes) {
      if (p === prefix) return spec.list;
      if (p.startsWith(prefix + "/")) {
        const rest = p.slice(prefix.length + 1);
        return rest.length > 0 && spec.detail ? spec.detail : spec.list;
      }
    }
  }
  return "Seite";
}

/** Aktuellen Router-`idx` aus dem History-State lesen (defensiv). */
export function readHistoryIdx(): number {
  if (typeof window === "undefined") return 0;
  const idx = (window.history.state as { idx?: unknown } | null | undefined)
    ?.idx;
  return typeof idx === "number" && idx >= 0 ? idx : 0;
}

export interface NavHistoryView {
  canGoBack: boolean;
  canGoForward: boolean;
  /** Grobes Ziel-Label für „Zurück" (Tooltip), oder null wenn deaktiviert. */
  backLabel: string | null;
  /** Grobes Ziel-Label für „Vorwärts" (Tooltip), oder null wenn deaktiviert. */
  forwardLabel: string | null;
}

/**
 * Hook: pflegt den In-App-Verlaufsstack und liefert die abgeleiteten Flags/Labels
 * für die Topbar-Buttons. Lauscht auf Location + NavigationType und verrechnet
 * jede Navigation genau einmal (Dedup über `location.key`, robust gegen den
 * StrictMode-Doppel-Effekt).
 */
export function useNavHistory(): NavHistoryView {
  const location = useLocation();
  const navType = useNavigationType();
  const [state, setState] =
    React.useState<NavHistoryState>(initialNavHistory);
  const lastKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (lastKeyRef.current === location.key) return;
    lastKeyRef.current = location.key;
    const ev: NavEvent = {
      type: navType,
      pathname: location.pathname,
      label: routeLabel(location.pathname),
      idx: readHistoryIdx(),
    };
    setState((s) => navHistoryReduce(s, ev));
  }, [location.key, location.pathname, navType]);

  return {
    canGoBack: canGoBack(state),
    canGoForward: canGoForward(state),
    backLabel: backLabel(state),
    forwardLabel: forwardLabel(state),
  };
}
